import { useEffect, useState } from 'react';
import { ArrowRight, CheckCircle2, Mountain, Warehouse, PackageCheck, ShieldCheck, Video, Ship } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { useCMS } from '../hooks/useCMS';
import { apiFetchJson } from '../lib/api';
import { getVisitorId } from '../lib/utils';
import { trackPageView, trackEvent } from '../lib/analytics';
import CountryPhoneInput from '../components/CountryPhoneInput';
import SceneDeck from '../components/SceneDeck';

// ============================================================
// /b2b 供貨商招募（SceneDeck 簡化按鈕版，規則 59）
// 六幕全銀幕：共鳴→市場→拆山→流程→信任→診斷表單
// 收口：免費出海診斷表單 → b2b_leads（後台跟進）
// ============================================================

const MARKETS = [
  { code: 'GB', label: '🇬🇧 英國' },
  { code: 'CA', label: '🇨🇦 加拿大' },
  { code: 'AU', label: '🇦🇺 澳洲' },
  { code: 'US', label: '🇺🇸 美國' },
  { code: 'OTHER', label: '🌍 其他' },
];

const PRODUCT_TYPES = [
  { value: 'food', label: '食品／飲品' },
  { value: 'health', label: '保健品' },
  { value: 'beauty', label: '美妝' },
  { value: 'daily', label: '日用品' },
  { value: 'other', label: '其他' },
];

const MOUNTAINS = [
  { mountain: '清關法規', mountainDesc: '食品敏感貨，驗關扣關有理說不清', bridge: '包稅專線一條龍', bridgeDesc: '食品、液體等敏感貨可走，清關 YYC 搞掂' },
  { mountain: '海外倉儲', mountainDesc: '自建海外倉，成本高、管理煩', bridge: '海外一件代發', bridgeDesc: '貨存 YYC 倉，賣一件發一件' },
  { mountain: '起訂量風險', mountainDesc: '傳統外貿起訂量大，壓死現金流', bridge: '零存貨壓力', bridgeDesc: '香港交收就當本地批發——賣出先出貨' },
];

const STEPS = [
  { n: '01', title: '貨交荃灣倉', desc: '香港交收就完成——唔使處理任何出口文件' },
  { n: '02', title: '坤哥直播試貨', desc: '真人試食拍片——內容即素材，信任即流量' },
  { n: '03', title: '平台＋小B 分銷', desc: '海外推廣者網絡幫你賣——觸達你接觸唔到嘅圈層' },
  { n: '04', title: '包稅直郵', desc: '一件代發直郵英加澳美——賣出先出貨' },
];

export default function B2BDiagnosis() {
  const { getValue } = useCMS();
  const [form, setForm] = useState({ contactName: '', whatsapp: '', brandName: '', productType: 'food', notesFromLead: '' });
  const [markets, setMarkets] = useState<string[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');
  const [slider, setSlider] = useState(40);

  useEffect(() => { trackPageView('/b2b'); }, []);

  const toggleMarket = (code: string) => {
    setMarkets((prev) => (prev.includes(code) ? prev.filter((m) => m !== code) : [...prev, code]));
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (submitting) return;
    setError('');
    setSubmitting(true);
    try {
      const res = await apiFetchJson<{ success: boolean; error?: string }>('/public/live-funnel/lead', {
        method: 'POST',
        body: JSON.stringify({ ...form, targetMarkets: markets, visitorId: getVisitorId(), source: 'b2b-diagnosis' }),
      });
      if (res.success) { trackEvent('live_funnel_lead_submitted', {}); setDone(true); }
      else setError(res.error || '提交失敗，請稍後再試');
    } catch { setError('提交失敗，請稍後再試'); }
    finally { setSubmitting(false); }
  };

  const hkPrice = 20 + slider;
  const overseasLow = Math.round(hkPrice * 1.8);
  const overseasHigh = Math.round(hkPrice * 2.8);
  const inputCls = 'w-full border border-stone-200 rounded-xl px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-x1493-primary/30';
  const labelCls = 'block text-sm font-bold text-stone-700 mb-1.5';

  // 六幕（SceneDeck 簡化按鈕版）
  const scenes = [
    {
      id: 'hook',
      render: () => (
        <section className="relative min-h-full bg-x1493-dark text-white flex items-center overflow-hidden">
          <img src="/assets/stock/story-g4-cranes.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(100deg, rgba(11,53,39,.95) 0%, rgba(11,53,39,.8) 55%, rgba(11,53,39,.45) 100%)' }} />
          <div className="max-w-3xl mx-auto px-6 py-20 relative z-10">
            <p className="text-x1493-secondary font-bold tracking-widest text-sm mb-4">給香港品牌老闆的一份話</p>
            <h1 className="text-3xl md:text-5xl font-bold leading-tight mb-6">
              舖租、人工、廣告——<br className="hidden md:block" />
              樣樣都喺食緊你嘅利潤。
            </h1>
            <p className="text-white/80 text-lg max-w-2xl leading-relaxed mb-2">
              香港 700 萬人嘅市場，你賣咗十五年年年難做——
            </p>
            <p className="text-x1493-secondary text-2xl md:text-3xl font-bold max-w-2xl leading-snug">
              但同一件貨，喺海外港人超市，賣緊兩三倍價。
            </p>
            <p className="text-white/50 text-sm mt-10 animate-pulse">▼ 撳下面按鈕，一頁一頁睇</p>
          </div>
        </section>
      ),
    },
    {
      id: 'market',
      render: () => (
        <section className="min-h-full bg-stone-50 flex items-center py-14">
          <div className="max-w-4xl mx-auto px-6 w-full">
            <h2 className="text-2xl md:text-3xl font-bold mb-3 text-center">海外港人——一個被低估嘅市場</h2>
            <p className="text-stone-500 text-center max-w-2xl mx-auto mb-8">
              移居英加澳美嘅港人以十萬計——佢哋消費力高、講廣東話、搵緊香港味，但當地港貨選擇少、價錢貴。
            </p>
            <div className="bg-white border border-stone-200 rounded-3xl p-6 md:p-10 mb-8">
              <p className="text-center text-xs font-bold text-stone-400 tracking-widest mb-6">拖動試吓——你嘅貨，海外賣幾多？</p>
              <div className="flex items-center justify-center gap-6 md:gap-12 mb-6">
                <div className="text-center">
                  <p className="text-xs text-stone-400 mb-2">香港零售</p>
                  <p className="text-3xl md:text-4xl font-black text-stone-700">HK${hkPrice}</p>
                </div>
                <ArrowRight size={24} className="text-x1493-secondary" />
                <div className="text-center">
                  <p className="text-xs text-stone-400 mb-2">海外港人市場</p>
                  <p className="text-3xl md:text-4xl font-black text-x1493-primary">HK${overseasLow}–{overseasHigh}</p>
                </div>
              </div>
              <input
                type="range"
                min={20}
                max={100}
                value={slider}
                onChange={(e) => setSlider(Number(e.target.value))}
                className="w-full max-w-md mx-auto block accent-x1493-primary"
                aria-label="調整香港零售價睇海外參考價"
              />
              <p className="text-center text-[11px] text-stone-400 mt-4">* 參考海外港貨超市普遍零售溢價，實際定價視乎產品與市場</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {[
                { n: '20萬+', t: '英國港人', d: 'BNO 移民潮後形成嘅新港人社群' },
                { n: '50萬+', t: '加拿大港人', d: '幾十年僑居根基，港貨需求穩定' },
                { n: '100萬+', t: '海外港人總社群', d: '分佈英加澳美——講廣東話嘅市場' },
              ].map((s, i) => (
                <div key={i} className="bg-stone-50 border border-stone-200 rounded-2xl p-5 text-center">
                  <p className="text-2xl font-black text-x1493-primary mb-1">{s.n}</p>
                  <p className="font-bold text-sm">{s.t}</p>
                  <p className="text-xs text-stone-500 mt-1">{s.d}</p>
                </div>
              ))}
            </div>
          </div>
        </section>
      ),
    },
    {
      id: 'mountains',
      render: () => (
        <section className="min-h-full bg-x1493-dark text-white flex items-center py-14">
          <div className="max-w-4xl mx-auto px-6 w-full">
            <div className="flex items-center gap-3 mb-8">
              <Mountain size={32} className="text-x1493-secondary" />
              <h2 className="text-2xl md:text-3xl font-bold">出海三座山——YYC 幫你搬</h2>
            </div>
            <div className="space-y-5">
              {MOUNTAINS.map((m, i) => (
                <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-5 md:p-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div className="flex items-start gap-3">
                      <span className="text-red-400 font-black text-lg mt-0.5">✕</span>
                      <div>
                        <p className="font-bold text-red-300">{m.mountain}</p>
                        <p className="text-sm text-white/60">{m.mountainDesc}</p>
                      </div>
                    </div>
                    <div className="flex items-start gap-3 border-t md:border-t-0 md:border-l border-white/10 pt-3 md:pt-0 md:pl-6">
                      <CheckCircle2 size={20} className="text-x1493-secondary mt-0.5 shrink-0" />
                      <div>
                        <p className="font-bold text-x1493-secondary">{m.bridge}</p>
                        <p className="text-sm text-white/70">{m.bridgeDesc}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      ),
    },
    {
      id: 'steps',
      render: () => (
        <section className="min-h-full bg-white flex items-center py-14">
          <div className="max-w-4xl mx-auto px-6 w-full">
            <div className="flex items-center gap-3 mb-8">
              <Warehouse size={32} className="text-x1493-primary" />
              <h2 className="text-2xl md:text-3xl font-bold">四步出海——你負責造貨，YYC 負責其他</h2>
            </div>
            <div className="space-y-4">
              {STEPS.map((s, i) => (
                <div key={s.n} className="flex items-start gap-5 bg-white border border-stone-200 rounded-2xl p-6">
                  <span className="text-2xl font-black text-x1493-primary/30 shrink-0">{s.n}</span>
                  <div className="flex-1">
                    <p className="font-bold text-lg mb-1">{s.title}</p>
                    <p className="text-sm text-stone-500">{s.desc}</p>
                  </div>
                  {i < STEPS.length - 1 && <ArrowRight size={20} className="text-stone-300 mt-1 hidden md:block shrink-0" />}
                </div>
              ))}
            </div>
          </div>
        </section>
      ),
    },
    {
      id: 'trust',
      render: () => (
        <section className="min-h-full bg-x1493-dark text-white flex items-center py-14">
          <div className="max-w-4xl mx-auto px-6 w-full">
            <div className="flex items-center gap-3 mb-8">
              <Video size={32} className="text-x1493-secondary" />
              <h2 className="text-2xl md:text-3xl font-bold">真人試過先上架——香港信任直播間</h2>
            </div>
            <div className="grid md:grid-cols-3 gap-5">
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <ShieldCheck size={24} className="text-x1493-secondary mb-3" />
                <p className="font-bold mb-1">信任輸出</p>
                <p className="text-sm text-white/70">海外港人唔信代購——但信香港門市、信坤哥試過、信檢測報告。</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <PackageCheck size={24} className="text-x1493-secondary mb-3" />
                <p className="font-bold mb-1">分銷網絡</p>
                <p className="text-sm text-white/70">平台推廣者網絡幫你觸達圈層——你唔使一個個客去搵。</p>
              </div>
              <div className="bg-white/5 border border-white/10 rounded-2xl p-5">
                <Ship size={24} className="text-x1493-secondary mb-3" />
                <p className="font-bold mb-1">物流兜底</p>
                <p className="text-sm text-white/70">包稅專線＋一件代發——你交貨，我哋送到門口。</p>
              </div>
            </div>
          </div>
        </section>
      ),
    },
    {
      id: 'form',
      render: () => (
        <section className="min-h-full bg-stone-50 flex items-center py-14">
          <div className="max-w-xl mx-auto px-6 w-full">
            <div className="text-center mb-8">
              <h2 className="text-2xl font-bold mb-2">{getValue('live.b2b.title', '免費出海診斷')}</h2>
              <p className="text-stone-500 text-sm">{getValue('live.b2b.subtitle', '留低基本資料，顧問一對一幫你計清航線、清關、代發成本。')}</p>
            </div>
            {done ? (
              <div className="bg-white border border-stone-200 rounded-3xl p-8 text-center">
                <CheckCircle2 size={44} className="text-green-600 mx-auto mb-4" />
                <h2 className="text-xl font-bold mb-2">{getValue('live.b2b.done_title', '收到！名額已為你鎖定')}</h2>
                <p className="text-sm text-stone-500 leading-relaxed">{getValue('live.b2b.done_body', '專屬顧問會盡快透過 WhatsApp 與你約時間診斷。')}</p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white border border-stone-200 rounded-3xl p-6 md:p-8 space-y-5">
                <div>
                  <label className={labelCls}>稱呼 *</label>
                  <input className={inputCls} value={form.contactName} onChange={(e) => setForm({ ...form, contactName: e.target.value })} placeholder="例：陳生／Kenny" required maxLength={60} />
                </div>
                <div>
                  <label className={labelCls}>WhatsApp 號碼 *</label>
                  <CountryPhoneInput value={form.whatsapp} onChange={(v) => setForm({ ...form, whatsapp: v })} inputCls={inputCls} required ariaLabel="WhatsApp 號碼" />
                </div>
                <div>
                  <label className={labelCls}>品牌／公司名稱</label>
                  <input className={inputCls} value={form.brandName} onChange={(e) => setForm({ ...form, brandName: e.target.value })} placeholder="例：某某醬料" maxLength={100} />
                </div>
                <div>
                  <label className={labelCls}>貨品類型</label>
                  <select className={inputCls} value={form.productType} onChange={(e) => setForm({ ...form, productType: e.target.value })}>
                    {PRODUCT_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                  </select>
                </div>
                <div>
                  <label className={labelCls}>目標市場（可多選）</label>
                  <div className="flex flex-wrap gap-2">
                    {MARKETS.map((m) => (
                      <button
                        type="button"
                        key={m.code}
                        onClick={() => toggleMarket(m.code)}
                        className={`text-sm px-3.5 py-2 rounded-full border-2 font-bold transition ${markets.includes(m.code) ? 'bg-x1493-primary text-white border-x1493-primary' : 'border-stone-200 text-stone-600 hover:border-x1493-primary'}`}
                      >
                        {m.label}
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className={labelCls}>想補充嘅（一兩句就得）</label>
                  <textarea className={inputCls} rows={3} value={form.notesFromLead} onChange={(e) => setForm({ ...form, notesFromLead: e.target.value })} placeholder="例：主打辣醬，想試英國市場" maxLength={500} />
                </div>
                {error && <p className="text-sm text-red-600">{error}</p>}
                <button type="submit" disabled={submitting} className="w-full bg-x1493-primary text-white font-bold py-3.5 rounded-2xl hover:brightness-110 transition disabled:opacity-60">
                  {submitting ? '提交中…' : getValue('live.b2b.submit_btn', '鎖定免費診斷名額')}
                </button>
                <p className="text-xs text-stone-400 text-center">{getValue('live.b2b.privacy_note', '資料只用於是次診斷聯絡，不會用作其他用途')}</p>
              </form>
            )}
          </div>
        </section>
      ),
    },
  ];

  return (
    <SceneDeck
      scenes={scenes}
      mode="button"
      labels={{ next: '下一頁', done: '免費出海診斷 →' }}
    />
  );
}
